import { ApiError } from '@saay/api-client';
import {
  buildSolarMonthGrid,
  formatCount,
  formatLocalDate,
  formatToman,
  localDateToSolarHijri,
  SOLAR_HIJRI_MONTHS_FA,
  statusLabel,
  todayBusinessDate,
  toPersianDigits,
  weekdayOf,
  type LocalDate,
} from '@saay/formatting';
import { CardFaceRows, ListRow, Money, SignatureCard, Surface } from '@saay/blocks';
import { useQueryClient } from '@tanstack/react-query';
import { LandmarkIcon, RefreshCwIcon, ShieldAlertIcon, WalletCardsIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, Outlet, useMatch, useNavigate, useParams, useSearchParams } from 'react-router';

import type {
  BusinessCalendarDay,
  CalendarSyncRun,
  CalendarSyncRunDetail,
  CalendarSyncRunAcceptance,
  ReconciliationItem,
  ReconciliationItemResolution,
  SettlementAttempt,
  SettlementBatch,
  SettlementBatchExecution,
  SettlementBatchExecutionCreate,
  SettlementAttemptRetry,
  SettlementHold,
  SettlementHoldRelease,
} from '../api.js';
import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { onSheetOpenChange, OPENED_FROM_LIST, useCloseSheet } from '../components/route-sheet.js';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  FactList,
  LoadMore,
  PageHeader,
  Section,
  Skeleton,
  StatusChip,
  Tabs,
} from '../kit.js';
import { canManageCalendarClosures, canManageSettlements } from '../permissions.js';
import {
  listStatus,
  setupKeys,
  useAdminSelf,
  useBusinessCalendarDays,
  useCalendarSyncRun,
  useCalendarSyncRuns,
  useReconciliationItems,
  useRecoveryEntries,
  useSettlementAttempts,
  useSettlementBatch,
  useSettlementBatches,
  useSettlementHolds,
} from '../queries.js';
import { BATCH_KIND_OPTIONS, CashBatches } from './cash-settlements.js';

type Tab = 'batches' | 'cash' | 'attempts' | 'holds' | 'recovery' | 'reconciliation';
type ResolutionType = 'MARK_PAYOUT_SUCCEEDED' | 'MARK_PAYOUT_FAILED' | 'NO_ACTION';

const WEEKDAY_HEADERS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'] as const;

function solarText(value: string | null | undefined): string {
  if (value === undefined || value === null || value === '') return '—';
  return formatLocalDate(value, 'long');
}

function shiftMonth(year: number, month: number, delta: number): { month: number; year: number } {
  let nextMonth = month + delta;
  let nextYear = year;
  while (nextMonth < 1) {
    nextMonth += 12;
    nextYear -= 1;
  }
  while (nextMonth > 12) {
    nextMonth -= 12;
    nextYear += 1;
  }
  return { month: nextMonth, year: nextYear };
}

function Frame({ tab }: { readonly tab: Tab }) {
  usePageTitle('تسویهٔ فروشگاه‌ها');
  const navigate = useNavigate();
  return (
    <RequireArea area="settlements">
      {() => (
        <>
          <PageHeader
            description="تعهد تسویه مستقل از وصول خریدار است."
            title="تسویهٔ فروشگاه‌ها"
          />
          <Tabs<Tab>
            label="بخش تسویه‌ها"
            onChange={(key) =>
              void navigate(key === 'batches' ? '/settlements' : `/settlements/${key}`)
            }
            panel={<Panel tab={tab} />}
            tabs={[
              { key: 'batches', label: 'دسته‌های هفتگی' },
              { key: 'cash', label: 'دسته‌های نقدی روزانه' },
              { key: 'attempts', label: 'تلاش‌های واریز' },
              { key: 'holds', label: 'نگه‌داری‌ها' },
              { key: 'recovery', label: 'بازیافت' },
              { key: 'reconciliation', label: 'مغایرت‌ها' },
            ]}
            value={tab}
          />
          <Outlet />
        </>
      )}
    </RequireArea>
  );
}

export function SettlementsPage() {
  return <Frame tab="batches" />;
}
export function SettlementCashPage() {
  return <Frame tab="cash" />;
}
export function SettlementAttemptsPage() {
  return <Frame tab="attempts" />;
}
export function SettlementHoldsPage() {
  return <Frame tab="holds" />;
}
export function SettlementRecoveryPage() {
  return <Frame tab="recovery" />;
}
export function SettlementReconciliationPage() {
  return <Frame tab="reconciliation" />;
}

function Panel({ tab }: { readonly tab: Tab }) {
  return tab === 'cash' ? (
    <CashBatches />
  ) : tab === 'attempts' ? (
    <Attempts />
  ) : tab === 'holds' ? (
    <Holds />
  ) : tab === 'recovery' ? (
    <Recovery />
  ) : tab === 'reconciliation' ? (
    <Reconciliation />
  ) : (
    <Batches />
  );
}

function Batches() {
  const navigate = useNavigate();
  const list = useSettlementBatches();
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const self = useAdminSelf().data;
  const onDetail = useMatch('/settlements/:batchId') !== null;
  const next = rows.find((row) => row.status === 'FINALIZED') ?? rows[0];
  const [execute, setExecute] = useState(false);
  const canExecute = self !== undefined && canManageSettlements(self);
  const columns: readonly DataTableColumn<(typeof rows)[number]>[] = [
    {
      header: 'هفته',
      id: 'week',
      render: (row) => (
        <Link state={OPENED_FROM_LIST} to={`/settlements/${row.id}`}>
          {solarText(row.weekStart)} تا {solarText(row.weekEnd)}
        </Link>
      ),
    },
    { header: 'واریز', id: 'date', render: (row) => solarText(row.adjustedExecutionDate) },
    { header: 'فروشگاه', id: 'merchants', render: (row) => formatCount(row.merchantCount) },
    { header: 'ناخالص', id: 'gross', render: (row) => <Money rials={row.grossIrr} /> },
    { header: 'کارمزد', id: 'fee', render: (row) => <Money rials={row.saayFeeIrr} /> },
    { header: 'خالص', id: 'net', render: (row) => <Money rials={row.netIrr} /> },
    {
      header: 'وضعیت',
      id: 'status',
      render: (row) => <StatusChip enumName="settlementBatchStatus" value={row.status} />,
    },
  ];
  return (
    <>
      {next === undefined ? null : (
        <SignatureCard label="دستهٔ بعدی" value={<Money rials={next.netIrr} size="lg" />}>
          <div className="mb-3">
            <StatusChip enumName="settlementBatchStatus" value={next.status} />
          </div>
          <CardFaceRows
            items={[
              { label: 'تاریخ قفل', value: solarText(next.anchorDate) },
              { label: 'تاریخ واریز', value: solarText(next.adjustedExecutionDate) },
              { label: 'فروشگاه‌ها', value: formatCount(next.merchantCount) },
            ]}
          />
          {canExecute && next.status === 'FINALIZED' && !onDetail ? (
            <Button
              className="mt-4 w-full rounded-full bg-brand-gold text-brand hover:bg-brand-gold/90"
              onClick={() => setExecute(true)}
            >
              اجرای واریز
            </Button>
          ) : null}
        </SignatureCard>
      )}
      <Section id="settlement-batches" title="دسته‌های هفتگی">
        <QueryState
          empty={<EmptyState title="دستهٔ تسویه‌ای ثبت نشده است" />}
          error={list.error}
          isEmpty={rows.length === 0}
          onRetry={() => void list.refetch()}
          skeleton={<Skeleton lines={5} />}
          status={listStatus(list)}
        >
          <AdminListTable
            caption="فهرست دسته‌های تسویه"
            columns={columns}
            mobileSummary={(row) => ({
              primary: formatToman(row.netIrr),
              secondary: <StatusChip enumName="settlementBatchStatus" value={row.status} />,
            })}
            onRowClick={(row) =>
              void navigate(`/settlements/${row.id}`, { state: OPENED_FROM_LIST })
            }
            regionLabel="جدول دسته‌های تسویه"
            rowKey={(row) => row.id}
            rows={rows}
          />
          <LoadMore
            hasMore={list.hasNextPage}
            loading={list.isFetchingNextPage}
            onLoadMore={() => void list.fetchNextPage()}
          />
        </QueryState>
      </Section>
      {execute && next !== undefined ? (
        <Execute id={next.id} onOpenChange={setExecute} version={next.version} />
      ) : null}
    </>
  );
}

export function SettlementBatchSheet() {
  const { batchId = '' } = useParams();
  const batch = useSettlementBatch(batchId);
  const close = useCloseSheet('/settlements');
  const self = useAdminSelf().data;
  return (
    <Dialog onOpenChange={onSheetOpenChange(close)} open size="wide" title="جزئیات دستهٔ تسویه">
      <QueryState
        error={batch.error}
        onRetry={() => void batch.refetch()}
        skeleton={<Skeleton lines={6} />}
        status={batch.status}
      >
        {batch.data === undefined ? null : (
          <BatchDetail
            batch={batch.data}
            canExecute={self !== undefined && canManageSettlements(self)}
          />
        )}
      </QueryState>
    </Dialog>
  );
}

function BatchDetail({
  batch,
  canExecute,
}: {
  readonly batch: SettlementBatch;
  readonly canExecute: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="admin-sections">
      <FactList
        items={[
          {
            key: 'week',
            label: 'هفته',
            value: `${solarText(batch.weekStart)} تا ${solarText(batch.weekEnd)}`,
          },
          { key: 'gross', label: 'ناخالص', value: formatToman(batch.grossIrr) },
          { key: 'net', label: 'خالص', value: formatToman(batch.netIrr) },
          {
            key: 'status',
            label: 'وضعیت',
            value: <StatusChip enumName="settlementBatchStatus" value={batch.status} />,
          },
        ]}
      />
      {canExecute && batch.status === 'FINALIZED' ? (
        <Button onClick={() => setOpen(true)}>اجرای واریز</Button>
      ) : null}
      <Section id="batch-lines" title="واریز فروشگاه‌ها">
        <ul className="flex flex-col gap-2">
          {batch.attempts.map((attempt, index) => (
            <li key={attempt.id}>
              <ListRow
                description={`شبا: ${attempt.shebaMasked ?? '—'} · ${formatToman(attempt.payoutAmountIrr)}`}
                icon={LandmarkIcon}
                title={`واریز ${formatCount(index + 1)}`}
                tone="olive"
                end={<StatusChip enumName="payoutAttemptStatus" value={attempt.status} />}
              />
            </li>
          ))}
        </ul>
      </Section>
      {open ? <Execute id={batch.id} onOpenChange={setOpen} version={batch.version} /> : null}
    </div>
  );
}

function Execute({
  id,
  onOpenChange,
  version,
}: {
  readonly id: string;
  readonly onOpenChange: (open: boolean) => void;
  readonly version: number;
}) {
  const qc = useQueryClient();
  return (
    <ReasonDialog<SettlementBatchExecution>
      name="settlement.execute"
      onOpenChange={onOpenChange}
      submitLabel="اجرای واریز"
      title="اجرای واریز"
      onDone={async () => qc.invalidateQueries({ queryKey: setupKeys.settlementBatches })}
      send={(client, reason, intent) =>
        client.post(`/admin/settlement-batches/${encodeURIComponent(id)}/execution`, {
          body: { expectedVersion: version, reason } satisfies SettlementBatchExecutionCreate,
          intent,
        })
      }
    />
  );
}

/** `?status=all`: without it the list keeps its failed-first default. */
const ALL_STATUSES = 'all';

function Attempts() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || 'FAILED';
  const batchKind = params.get('batchKind') ?? '';
  const list = useSettlementAttempts(
    status === ALL_STATUSES ? undefined : status,
    batchKind === '' ? undefined : batchKind,
  );
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const self = useAdminSelf().data;

  function patch(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    setParams(next);
  }

  return (
    <Section id="settlement-attempts" title="تلاش‌های واریز">
      <FilterPillBar
        activeFilterCount={batchKind === '' ? 0 : 1}
        label="فیلتر تلاش‌های واریز"
        onClearAll={() => patch('batchKind', '')}
      >
        <Button
          className="h-9 shrink-0 rounded-full px-3 text-sm"
          onClick={() => patch('status', status === 'FAILED' ? ALL_STATUSES : '')}
          type="button"
          variant="secondary"
        >
          {status === 'FAILED' ? 'همهٔ وضعیت‌ها' : 'فقط ناموفق'}
        </Button>
        <FilterPill
          label="نوع دسته"
          onChange={(value) => patch('batchKind', value)}
          options={BATCH_KIND_OPTIONS}
          value={batchKind}
        />
      </FilterPillBar>
      <QueryState
        empty={<EmptyState title="تلاشی پیدا نشد" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton />}
        status={listStatus(list)}
      >
        {status === 'FAILED' ? (
          <p className="mb-3 text-sm text-muted-foreground">
            ابتدا تلاش‌های ناموفق نمایش داده می‌شوند.
          </p>
        ) : null}
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id}>
              <Attempt attempt={row} canManage={self !== undefined && canManageSettlements(self)} />
            </li>
          ))}
        </ul>
      </QueryState>
    </Section>
  );
}

function Attempt({
  attempt,
  canManage,
}: {
  readonly attempt: SettlementAttempt;
  readonly canManage: boolean;
}) {
  const [detail, setDetail] = useState(false);
  const [retry, setRetry] = useState(false);
  const qc = useQueryClient();
  return (
    <>
      <ListRow
        description={`${formatToman(attempt.payoutAmountIrr)}، ${statusLabel('settlementBatchKind', attempt.batchKind)}`}
        icon={WalletCardsIcon}
        title="تلاش واریز"
        tone="copper"
        end={
          <>
            <Button onClick={() => setDetail(true)} size="sm" variant="secondary">
              جزئیات
            </Button>
            {canManage && attempt.status === 'FAILED' ? (
              <Button onClick={() => setRetry(true)} size="sm" variant="secondary">
                تلاش دوباره
              </Button>
            ) : null}
            <StatusChip enumName="payoutAttemptStatus" value={attempt.status} />
          </>
        }
      />
      {detail ? (
        <Dialog
          footer={
            <Button onClick={() => setDetail(false)} variant="secondary">
              بستن
            </Button>
          }
          onOpenChange={setDetail}
          open
          size="small"
          title="جزئیات تلاش"
        >
          <FactList
            items={[
              {
                key: 'kind',
                label: 'نوع دسته',
                value: statusLabel('settlementBatchKind', attempt.batchKind),
              },
              {
                key: 'gross',
                label: 'ناخالص',
                value: formatToman(attempt.grossPayableIrr ?? attempt.payoutAmountIrr),
              },
              {
                key: 'offset',
                label: 'کسر بازیافت',
                value: formatToman(attempt.recoveryOffsetIrr),
              },
              {
                key: 'status',
                label: 'وضعیت',
                value: <StatusChip enumName="payoutAttemptStatus" value={attempt.status} />,
              },
            ]}
          />
        </Dialog>
      ) : null}
      {retry ? (
        <ReasonDialog<SettlementAttempt>
          name="settlement.retry"
          onOpenChange={setRetry}
          submitLabel="تلاش دوباره"
          title="تلاش دوباره"
          onDone={async () => qc.invalidateQueries({ queryKey: setupKeys.settlementAttempts })}
          send={(client, reason, intent) =>
            client.post(`/admin/settlement-attempts/${encodeURIComponent(attempt.id)}/retry`, {
              body: {
                expectedVersion: attempt.version ?? 0,
                reason,
              } satisfies SettlementAttemptRetry,
              intent,
            })
          }
        />
      ) : null}
    </>
  );
}

function Holds() {
  const list = useSettlementHolds();
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const self = useAdminSelf().data;
  return (
    <Section id="settlement-holds" title="نگه‌داری‌های فعال">
      <QueryState
        empty={<EmptyState title="نگه‌داری فعالی نیست" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton />}
        status={listStatus(list)}
      >
        <ul className="flex flex-col gap-2">
          {rows.map((hold) => (
            <li key={hold.id}>
              <Hold canManage={self !== undefined && canManageSettlements(self)} hold={hold} />
            </li>
          ))}
        </ul>
      </QueryState>
    </Section>
  );
}

function Hold({ hold, canManage }: { readonly hold: SettlementHold; readonly canManage: boolean }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  return (
    <>
      <ListRow
        description={hold.reason ?? '—'}
        icon={ShieldAlertIcon}
        title="نگه‌داری تسویه"
        tone="gold"
        end={
          <>
            {canManage && hold.status === 'ACTIVE' ? (
              <Button onClick={() => setOpen(true)} size="sm" variant="secondary">
                برداشتن
              </Button>
            ) : null}
            <StatusChip enumName="settlementHoldStatus" value={hold.status} />
          </>
        }
      />
      {open ? (
        <ReasonDialog<SettlementHold>
          name="settlement.release"
          onOpenChange={setOpen}
          submitLabel="برداشتن"
          title="برداشتن نگه‌داری"
          onDone={async () => qc.invalidateQueries({ queryKey: setupKeys.settlementHolds })}
          send={(client, reason, intent) =>
            client.post(`/admin/settlement-holds/${encodeURIComponent(hold.id)}/release`, {
              body: { expectedVersion: hold.version ?? 0, reason } satisfies SettlementHoldRelease,
              intent,
            })
          }
        />
      ) : null}
    </>
  );
}

function Recovery() {
  const list = useRecoveryEntries();
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <Section id="merchant-recovery" title="بازیافت فروشگاه">
      <QueryState
        empty={<EmptyState title="موردی برای بازیافت نیست" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton />}
        status={listStatus(list)}
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id}>
              <ListRow
                description={row.evidenceReference ?? row.reason ?? '—'}
                icon={LandmarkIcon}
                title={formatToman(row.amountIrr)}
                tone="clay"
                end={
                  <>
                    <StatusChip enumName="merchantRecoveryEntryType" value={row.entryType} />
                    {row.status === null ? null : (
                      <StatusChip enumName="merchantRecoveryStatus" value={row.status} />
                    )}
                  </>
                }
              />
            </li>
          ))}
        </ul>
      </QueryState>
    </Section>
  );
}

function Reconciliation() {
  const list = useReconciliationItems();
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const self = useAdminSelf().data;
  return (
    <Section id="settlement-reconciliation" title="اقلام مغایرت">
      <QueryState
        empty={<EmptyState title="مغایرتی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton />}
        status={listStatus(list)}
      >
        <ul className="flex flex-col gap-2">
          {rows.map((item) => (
            <li key={item.id}>
              <Reconcile canManage={self !== undefined && canManageSettlements(self)} item={item} />
            </li>
          ))}
        </ul>
      </QueryState>
    </Section>
  );
}

function Reconcile({
  item,
  canManage,
}: {
  readonly item: ReconciliationItem;
  readonly canManage: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [resolution, setResolution] = useState<ResolutionType>('MARK_PAYOUT_SUCCEEDED');
  const [reference, setReference] = useState('');
  const qc = useQueryClient();
  const choices: readonly (readonly [ResolutionType, string])[] =
    item.itemType === 'PAYOUT_RETURNED'
      ? [
          ['MARK_PAYOUT_FAILED', 'ثبت ناموفق'],
          ['NO_ACTION', 'بدون اقدام'],
        ]
      : [
          ['MARK_PAYOUT_SUCCEEDED', 'ثبت موفق'],
          ['MARK_PAYOUT_FAILED', 'ثبت ناموفق'],
          ['NO_ACTION', 'بدون اقدام'],
        ];
  return (
    <>
      <ListRow
        description={item.providerReference ?? 'بدون مرجع ارائه‌دهنده'}
        icon={RefreshCwIcon}
        title={item.amountIrr === undefined ? 'مغایرت' : formatToman(item.amountIrr)}
        tone="copper"
        end={
          <>
            {item.itemType === undefined ? null : (
              <StatusChip enumName="reconciliationItemType" value={item.itemType} />
            )}
            {canManage && item.status === 'OPEN' ? (
              <Button onClick={() => setOpen(true)} size="sm" variant="secondary">
                رفع مغایرت
              </Button>
            ) : null}
            <StatusChip enumName="reconciliationItemStatus" value={item.status} />
          </>
        }
      />
      {open ? (
        <ReasonDialog<ReconciliationItem>
          name="reconciliation.resolve"
          onOpenChange={setOpen}
          submitLabel="ثبت نتیجه"
          title="رفع مغایرت"
          onDone={async () => qc.invalidateQueries({ queryKey: setupKeys.reconciliation })}
          send={(client, reason, intent) => {
            if (resolution === 'MARK_PAYOUT_SUCCEEDED' && reference.trim() === '') {
              throw new ApiError({
                code: 'REQUEST_INVALID',
                messageFa: 'مرجع بانکی را وارد کنید.',
                requestId: null,
                status: 400,
              });
            }
            return client.post(
              `/admin/reconciliation-items/${encodeURIComponent(item.id)}/resolution`,
              {
                body: {
                  expectedVersion: item.version ?? 0,
                  reason,
                  resolutionType: resolution,
                  ...(resolution === 'MARK_PAYOUT_SUCCEEDED'
                    ? {
                        providerEvidence: {
                          reference: reference.trim(),
                          verifiedAt: new Date().toISOString(),
                        },
                      }
                    : {}),
                } satisfies ReconciliationItemResolution,
                intent,
              },
            );
          }}
        >
          <label className="grid gap-2 text-sm">
            نتیجه
            <select
              aria-label="نتیجه"
              className="rounded-md border bg-background p-2"
              onChange={(event) => setResolution(event.target.value as ResolutionType)}
              value={resolution}
            >
              {choices.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          {resolution === 'MARK_PAYOUT_SUCCEEDED' ? (
            <label className="grid gap-2 text-sm">
              مرجع بانکی
              <input
                aria-label="مرجع بانکی"
                className="rounded-md border bg-background p-2"
                onChange={(event) => setReference(event.target.value)}
                required
                value={reference}
              />
            </label>
          ) : null}
        </ReasonDialog>
      ) : null}
    </>
  );
}

export function CalendarPage() {
  usePageTitle('تقویم کاری');
  return (
    <RequireArea area="calendar">
      {(self) => (
        <Calendar canClose={canManageCalendarClosures(self)} canSync={canManageSettlements(self)} />
      )}
    </RequireArea>
  );
}

function Calendar({
  canClose,
  canSync,
}: {
  readonly canClose: boolean;
  readonly canSync: boolean;
}) {
  const today = todayBusinessDate();
  const initial = localDateToSolarHijri(today);
  const [cursor, setCursor] = useState({ month: initial.month, year: initial.year });
  const grid = buildSolarMonthGrid(cursor);
  const inMonth = grid.weeks.flat().filter((cell) => cell.inMonth);
  const from = inMonth[0]?.localDate ?? today;
  const to = inMonth[inMonth.length - 1]?.localDate ?? today;
  const days = useBusinessCalendarDays(from, to);
  const runs = useCalendarSyncRuns();
  const [overrides, setOverrides] = useState<ReadonlyMap<string, BusinessCalendarDay>>(new Map());
  const [withdrawn, setWithdrawn] = useState<ReadonlySet<string>>(new Set());
  const server = new Map(days.data?.days.map((day) => [day.date, day]) ?? []);
  const [date, setDate] = useState<LocalDate | null>(null);
  const pending = runs.data?.items.find((run) => run.status === 'REVIEW_PENDING');
  const monthTitle = `${SOLAR_HIJRI_MONTHS_FA[cursor.month - 1] ?? ''} ${toPersianDigits(cursor.year)}`;

  function dayOf(value: LocalDate): BusinessCalendarDay | undefined {
    if (withdrawn.has(value)) return undefined;
    return overrides.get(value) ?? server.get(value);
  }

  return (
    <>
      <PageHeader
        description={
          // D-105: until the setting is on, the calendar does not move any payout date.
          days.data?.enforced === false
            ? 'تقویم فعلاً روی تاریخ پرداخت‌ها اثری ندارد.'
            : 'جمعه‌ها، تعطیلات رسمی و تعطیلی‌های دستی قابل پرداخت نیستند.'
        }
        title="تقویم کاری"
      />
      <Banner tone="info">
        این تقویم فقط برای تسویهٔ اقساط است. فروش نقدی هر روز واریز می‌شود، حتی جمعه‌ها و تعطیلات.
      </Banner>
      <QueryState
        error={days.error}
        onRetry={() => void days.refetch()}
        skeleton={<Skeleton lines={6} />}
        status={days.status}
      >
        <Surface>
          <div className="flex items-center justify-between gap-2">
            <Button
              onClick={() => setCursor((current) => shiftMonth(current.year, current.month, -1))}
              type="button"
              variant="secondary"
            >
              ماه قبل
            </Button>
            <h2 className="text-lg font-bold">{monthTitle}</h2>
            <Button
              onClick={() => setCursor((current) => shiftMonth(current.year, current.month, 1))}
              type="button"
              variant="secondary"
            >
              ماه بعد
            </Button>
          </div>
          <div aria-label="تقویم کاری" className="mt-4 grid gap-1" role="grid">
            <div
              className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground"
              role="row"
            >
              {WEEKDAY_HEADERS.map((weekday) => (
                <span className="py-1" key={weekday} role="columnheader">
                  {weekday}
                </span>
              ))}
            </div>
            {grid.weeks.map((week) => (
              <div className="grid grid-cols-7 gap-1" key={week[0]?.localDate} role="row">
                {week.map((cell) => {
                  const record = dayOf(cell.localDate);
                  const closed = cell.isFriday || record?.isPayable === false;
                  const manual = record?.source === 'MANUAL' && record.isPayable === false;
                  const holiday = !cell.isFriday && !manual && record?.isPayable === false;
                  const tone = !cell.inMonth
                    ? 'bg-muted/40 text-muted-foreground'
                    : cell.isFriday
                      ? 'bg-tone-gold text-tone-gold-foreground'
                      : manual
                        ? 'bg-tone-copper text-tone-copper-foreground'
                        : holiday
                          ? 'bg-tone-clay text-tone-clay-foreground'
                          : 'bg-muted';
                  const todayMark = cell.localDate === today ? 'ring-2 ring-brand' : '';
                  return (
                    <button
                      aria-current={cell.localDate === today ? 'date' : undefined}
                      aria-label={formatLocalDate(cell.localDate, 'long')}
                      className={`grid min-h-11 place-items-center rounded-xl text-sm ${tone} ${todayMark}`}
                      disabled={!cell.inMonth}
                      key={cell.localDate}
                      onClick={() => {
                        if (cell.inMonth) setDate(cell.localDate);
                      }}
                      role="gridcell"
                      type="button"
                    >
                      {toPersianDigits(cell.solar.day)}
                      <span className="sr-only">{closed ? 'غیرقابل پرداخت' : 'پرداخت‌پذیر'}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <ul aria-label="راهنمای تقویم" className="mt-4 flex flex-wrap gap-3 text-xs">
            <li className="flex items-center gap-1">
              <span className="size-3 rounded-sm bg-tone-gold" />
              جمعه
            </li>
            <li className="flex items-center gap-1">
              <span className="size-3 rounded-sm bg-tone-clay" />
              تعطیل رسمی
            </li>
            <li className="flex items-center gap-1">
              <span className="size-3 rounded-sm bg-tone-copper" />
              تعطیلی دستی
            </li>
            <li className="flex items-center gap-1">
              <span className="size-3 rounded-sm ring-2 ring-brand" />
              امروز
            </li>
          </ul>
        </Surface>
      </QueryState>
      <Section id="calendar-sync" title="همگام‌سازی تقویم">
        <p className="text-sm text-muted-foreground">
          پوشش ثبت‌شده: {solarText(days.data?.coverage.populatedThrough)}
        </p>
        {canSync ? <SyncStart pending={pending} /> : null}
        <QueryState
          empty={<EmptyState title="اجرای همگام‌سازی ثبت نشده است" />}
          error={runs.error}
          isEmpty={(runs.data?.items.length ?? 0) === 0}
          onRetry={() => void runs.refetch()}
          skeleton={<Skeleton />}
          status={runs.status}
        >
          <ul className="mt-4 flex flex-col gap-2">
            {(runs.data?.items ?? []).map((run) => (
              <li key={run.id}>
                <Run canAccept={canSync} run={run} />
              </li>
            ))}
          </ul>
        </QueryState>
      </Section>
      {date === null ? null : (
        <DayDetail
          canClose={canClose}
          date={date}
          day={dayOf(date)}
          onCreated={(day) => {
            setWithdrawn((current) => {
              const next = new Set(current);
              next.delete(day.date);
              return next;
            });
            setOverrides((current) => new Map(current).set(day.date, day));
          }}
          onOpenChange={(open) => {
            if (!open) setDate(null);
          }}
          onWithdrawn={(value) => {
            setOverrides((current) => {
              const next = new Map(current);
              next.delete(value);
              return next;
            });
            setWithdrawn((current) => new Set(current).add(value));
          }}
        />
      )}
    </>
  );
}

function DayDetail({
  canClose,
  date,
  day,
  onCreated,
  onOpenChange,
  onWithdrawn,
}: {
  readonly canClose: boolean;
  readonly date: LocalDate;
  readonly day: BusinessCalendarDay | undefined;
  readonly onCreated: (day: BusinessCalendarDay) => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly onWithdrawn: (date: LocalDate) => void;
}) {
  const today = todayBusinessDate();
  const future = date > today;
  const friday = weekdayOf(date) === 'جمعه';
  const manual = day?.source === 'MANUAL' && day.isPayable === false;
  const official = !friday && day?.source === 'OFFICIAL_SYNC' && day.isPayable === false;
  const [action, setAction] = useState<'add' | 'withdraw' | null>(null);
  return (
    <Dialog onOpenChange={onOpenChange} open size="small" title="جزئیات روز">
      <FactList
        items={[
          { key: 'date', label: 'تاریخ', value: solarText(date) },
          { key: 'weekday', label: 'روز هفته', value: weekdayOf(date) },
          {
            key: 'payable',
            label: 'پرداخت',
            value: friday || day?.isPayable === false ? 'غیرقابل پرداخت' : 'پرداخت‌پذیر',
          },
          { key: 'name', label: 'مناسبت', value: day?.holidayNameFa ?? day?.reasonFa ?? '—' },
          {
            key: 'source',
            label: 'منبع',
            value:
              day?.source === undefined ? (
                '—'
              ) : (
                <StatusChip enumName="businessCalendarSource" value={day.source} />
              ),
          },
        ]}
      />
      {canClose && future && !friday && !official && !manual ? (
        <Button onClick={() => setAction('add')}>ثبت تعطیلی</Button>
      ) : null}
      {canClose && future && manual ? (
        <Button onClick={() => setAction('withdraw')} variant="secondary">
          برداشتن تعطیلی
        </Button>
      ) : null}
      {action === 'add' ? (
        <Closure
          date={date}
          onCreated={onCreated}
          onOpenChange={(open) => {
            if (!open) setAction(null);
          }}
        />
      ) : null}
      {action === 'withdraw' ? (
        <Withdraw
          date={date}
          onOpenChange={(open) => {
            if (!open) setAction(null);
          }}
          onWithdrawn={onWithdrawn}
        />
      ) : null}
    </Dialog>
  );
}

function Closure({
  date,
  onCreated,
  onOpenChange,
}: {
  readonly date: LocalDate;
  readonly onCreated: (day: BusinessCalendarDay) => void;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  return (
    <ReasonDialog<BusinessCalendarDay>
      name="calendar.close"
      onOpenChange={onOpenChange}
      reasonLabel="دلیل تعطیلی"
      submitLabel="ثبت تعطیلی"
      title="تعطیلی دستی روز"
      onDone={async (result) => {
        onCreated(result.data);
        await qc.invalidateQueries({ queryKey: setupKeys.calendarDays });
      }}
      send={(client, reason, intent) =>
        client.post('/admin/business-calendar/manual-entries', {
          body: { date, isPayable: false, reasonFa: reason },
          intent,
        })
      }
    >
      <p>{solarText(date)}</p>
    </ReasonDialog>
  );
}

function Withdraw({
  date,
  onOpenChange,
  onWithdrawn,
}: {
  readonly date: LocalDate;
  readonly onOpenChange: (open: boolean) => void;
  readonly onWithdrawn: (date: LocalDate) => void;
}) {
  const qc = useQueryClient();
  return (
    <ReasonDialog<undefined>
      name="calendar.withdraw"
      onOpenChange={onOpenChange}
      submitLabel="برداشتن تعطیلی"
      title="برداشتن تعطیلی"
      onDone={async () => {
        onWithdrawn(date);
        await qc.invalidateQueries({ queryKey: setupKeys.calendarDays });
      }}
      send={(client, reason, intent) =>
        client.post(
          `/admin/business-calendar/manual-entries/${encodeURIComponent(date)}/withdrawal`,
          { body: { reason }, intent },
        )
      }
    >
      <p>{solarText(date)}</p>
    </ReasonDialog>
  );
}

function SyncStart({ pending }: { readonly pending: CalendarSyncRun | undefined }) {
  const [open, setOpen] = useState(false);
  const [accept, setAccept] = useState(false);
  const [year, setYear] = useState(
    String(pending?.solarYear ?? localDateToSolarHijri(todayBusinessDate()).year),
  );
  const qc = useQueryClient();
  return (
    <>
      {open ? (
        <ReasonDialog<CalendarSyncRun>
          name="calendar.sync"
          onOpenChange={setOpen}
          required={false}
          submitLabel="شروع همگام‌سازی"
          title="شروع همگام‌سازی"
          onDone={async () => qc.invalidateQueries({ queryKey: setupKeys.calendarRuns })}
          send={(client, _reason, intent) =>
            client.post('/admin/business-calendar/sync-runs', {
              body: { solarYear: Number(year) },
              intent,
            })
          }
        >
          <label className="grid gap-2 text-sm">
            سال شمسی
            <input
              aria-label="سال شمسی"
              className="rounded-md border bg-background p-2"
              onChange={(event) => setYear(event.target.value)}
              value={year}
            />
          </label>
          {pending === undefined ? null : (
            <RunDiff onAccept={() => setAccept(true)} run={pending} />
          )}
        </ReasonDialog>
      ) : null}
      {accept && pending !== undefined ? <Accept onOpenChange={setAccept} run={pending} /> : null}
      <Button className="mt-3" onClick={() => setOpen(true)}>
        شروع همگام‌سازی
      </Button>
    </>
  );
}

function RunDiff({
  run,
  onAccept,
}: {
  readonly onAccept?: () => void;
  readonly run: CalendarSyncRun;
}) {
  return (
    <div className="grid gap-2 text-sm">
      <p>
        بازهٔ بررسی: {solarText(run.rangeStart)} تا {solarText(run.rangeEnd)}
      </p>
      <p>پوشش پس از پذیرش: {solarText(run.populatedThrough)}</p>
      <StatusChip enumName="calendarSyncRunStatus" value={run.status} />
      <p>تفاوت‌ها را پیش از پذیرش بررسی کنید.</p>
      {onAccept !== undefined && run.status === 'REVIEW_PENDING' ? (
        <Button onClick={onAccept} type="button">
          پذیرش تغییرات
        </Button>
      ) : null}
    </div>
  );
}

function Run({ run, canAccept }: { readonly run: CalendarSyncRun; readonly canAccept: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ListRow
        description={`${solarText(run.rangeStart)} تا ${solarText(run.rangeEnd)}`}
        icon={RefreshCwIcon}
        title={`همگام‌سازی ${toPersianDigits(String(run.solarYear ?? ''))}`}
        tone="forest"
        end={
          <>
            {canAccept && run.status === 'REVIEW_PENDING' ? (
              <Button onClick={() => setOpen(true)} size="sm">
                پذیرش تغییرات
              </Button>
            ) : null}
            <StatusChip enumName="calendarSyncRunStatus" value={run.status} />
          </>
        }
      />
      {open ? <Accept onOpenChange={setOpen} run={run} /> : null}
    </>
  );
}

function Accept({
  onOpenChange,
  run,
}: {
  readonly onOpenChange: (open: boolean) => void;
  readonly run: CalendarSyncRun;
}) {
  const detail = useCalendarSyncRun(run.id);

  if (detail.data === undefined) {
    return (
      <Dialog onOpenChange={onOpenChange} open size="small" title="پذیرش تغییرات تقویم">
        <QueryState
          error={detail.error}
          onRetry={() => void detail.refetch()}
          skeleton={<Skeleton lines={4} />}
          status={detail.status}
        >
          {null}
        </QueryState>
      </Dialog>
    );
  }

  if (detail.data.snapshotHash === null) {
    return (
      <Dialog onOpenChange={onOpenChange} open size="small" title="پذیرش تغییرات تقویم">
        <p>این همگام‌سازی دیگر آمادهٔ پذیرش نیست. صفحه را تازه‌سازی کنید.</p>
      </Dialog>
    );
  }

  return <AcceptDialog detail={detail.data} onOpenChange={onOpenChange} />;
}

function AcceptDialog({
  detail,
  onOpenChange,
}: {
  readonly detail: CalendarSyncRunDetail;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  return (
    <ReasonDialog<CalendarSyncRun>
      name="calendar.accept"
      onOpenChange={onOpenChange}
      reasonLabel="یادداشت پذیرش"
      required={false}
      submitLabel="پذیرش تغییرات"
      title="پذیرش تغییرات تقویم"
      onDone={async () => {
        await qc.invalidateQueries({ queryKey: setupKeys.calendarRuns });
        await qc.invalidateQueries({ queryKey: setupKeys.calendarRun(detail.id) });
      }}
      send={(client, _reason, intent) =>
        client.post(
          `/admin/business-calendar/sync-runs/${encodeURIComponent(detail.id)}/acceptance`,
          {
            body: {
              expectedVersion: detail.version,
              snapshotHash: detail.snapshotHash!,
            } satisfies CalendarSyncRunAcceptance,
            intent,
          },
        )
      }
    >
      <RunDiff run={detail} />
      <CalendarChanges days={detail.days} />
    </ReasonDialog>
  );
}

function CalendarChanges({ days }: { readonly days: CalendarSyncRunDetail['days'] }) {
  const changes = days?.filter((day) => day.change !== 'UNCHANGED') ?? [];
  if (changes.length === 0) {
    return <p className="text-sm text-muted-foreground">تغییری برای اعمال نیست.</p>;
  }

  return (
    <div className="grid gap-2 text-sm">
      <p>این روزها به تقویم اضافه می‌شوند یا نام تعطیلی‌شان عوض می‌شود:</p>
      <ul className="grid gap-1 list-disc ps-5">
        {changes.map((day) => (
          <li key={day.date}>
            {day.change === 'NEW' ? 'تعطیلی جدید' : 'نام تعطیلی تغییر کرده'}: {solarText(day.date)}{' '}
            — {day.holidayNameFa}
          </li>
        ))}
      </ul>
    </div>
  );
}
